import { formatMoney } from 'lib/functions/utils';

import {
  CalculatedLine,
  DiffLine,
  DiffResult,
  SavedEntry,
  SavedEntryLine,
} from './types';

export const generateDiff = (
  savedEntry: SavedEntry,
  calculatedLines: CalculatedLine[],
): DiffResult => {
  const diffLines: DiffLine[] = [];

  const savedMap = new Map<string, SavedEntryLine>();
  const calculatedMap = new Map<string, CalculatedLine>();

  savedEntry.lines.forEach((line) => {
    const key = `${line.node?.accountCode || ''}_${line.documentKey || ''}`;
    savedMap.set(key, line);
  });

  calculatedLines.forEach((line) => {
    const key = `${line.account || ''}_${line.documentId || ''}`;
    calculatedMap.set(key, line);
  });

  const allKeys = new Set([...savedMap.keys(), ...calculatedMap.keys()]);

  let totalDifferences = 0;

  allKeys.forEach((compositeKey) => {
    const savedLine = savedMap.get(compositeKey);
    const calculatedLine = calculatedMap.get(compositeKey);

    const savedDebit = savedLine?.debit ?? null;
    const savedCredit = savedLine?.credit ?? null;
    const calculatedDebit = calculatedLine?.debit ?? null;
    const calculatedCredit = calculatedLine?.credit ?? null;

    const rawDebitDiff = (calculatedDebit ?? 0) - (savedDebit ?? 0);
    const rawCreditDiff = (calculatedCredit ?? 0) - (savedCredit ?? 0);
    const debitDiff = Math.round(rawDebitDiff * 100) / 100 || 0;
    const creditDiff = Math.round(rawCreditDiff * 100) / 100 || 0;

    const hasDifference = debitDiff !== 0 || creditDiff !== 0;

    if (hasDifference) totalDifferences++;

    diffLines.push({
      accountCode:
        savedLine?.node?.accountCode || calculatedLine?.account || '',
      accountName: savedLine?.node?.key || calculatedLine?.accountName || '',
      documentKey: savedLine?.documentKey || calculatedLine?.documentId || '',
      savedDebit,
      savedCredit,
      calculatedDebit,
      calculatedCredit,
      hasDifference,
      debitDiff,
      creditDiff,
    });
  });

  diffLines.sort((a, b) => {
    const codeCompare = a.accountCode.localeCompare(b.accountCode);
    if (codeCompare !== 0) return codeCompare;
    return a.documentKey.localeCompare(b.documentKey);
  });

  const savedTotalDebit = savedEntry.lines.reduce((sum, l) => sum + l.debit, 0);
  const savedTotalCredit = savedEntry.lines.reduce(
    (sum, l) => sum + l.credit,
    0,
  );
  const calculatedTotalDebit = calculatedLines.reduce(
    (sum, l) => sum + l.debit,
    0,
  );
  const calculatedTotalCredit = calculatedLines.reduce(
    (sum, l) => sum + l.credit,
    0,
  );

  const totalDebitDiff =
    Math.round((calculatedTotalDebit - savedTotalDebit) * 100) / 100 || 0;
  const totalCreditDiff =
    Math.round((calculatedTotalCredit - savedTotalCredit) * 100) / 100 || 0;

  return {
    lines: diffLines,
    totalDifferences,
    savedTotalDebit,
    savedTotalCredit,
    calculatedTotalDebit,
    calculatedTotalCredit,
    debitDiff: totalDebitDiff,
    creditDiff: totalCreditDiff,
  };
};

export const formatDiff = (diff: number): string => {
  if (diff === 0) return '';
  const sign = diff > 0 ? '+' : '';
  return `(${sign}${formatMoney(diff)})`;
};
